<template>
    <div class="sidebar__section" :class="[{'sidebar__section--border-top-huge': mode === 'summary', 'sidebar__section--border-bottom': mode === 'full'}]" v-if="userLoggedIn">
        <div class="saved-routes">
            <div class="saved-routes__header" v-if="mode === 'summary'">
                <div class="saved-routes__header__title h2">Gespeicherte Routen</div>
            </div>
            <div class="saved-routes__intro copy-light" v-else>
                Die ersten drei Routen werden direkt auf der Startseite angezeigt.
            </div>
            <div class="saved-routes__list" ref="saved-routes-list" v-if="savedRoutes && savedRoutes.length > 0">
                <div class="saved-routes__route" v-for="route in routes" :key="route.id" @click="routeClickHandler(route)">
                    <div class="saved-routes__route__data">
                        <div class="saved-routes__route__data__icon">
                            <Icon><TransportModeIcon :mode="route.mode"/></Icon>
                        </div>
                        <div class="saved-routes__route__data__info">
                            <div class="saved-routes__route__data__info__name h3">
                                <icon v-if="route.hasTrafficAlert" class="saved-routes__route__data__info__name__icon" :size="16"><IconBell/></icon>
                                {{ route.name }}
                            </div>
                            <WaypointSummary :waypoints="route.waypoints" :fadeCountry="true" />
                        </div>
                    </div>
                    <div class="saved-routes__route__actions" @click="actionsClickHandler">
                        <SmrTooltip content="Ziehen, um Reihenfolge zu ändern" position="bottom-start" v-if="sortable">
                            <icon class="saved-routes__route__drag-handle" :width="20" :height="20" view-box="0 0 20 20" v-if="mode === 'full'"><IconDragHandle /></icon>
                        </SmrTooltip>    
                        <MoreActions 
                            v-if="!loadingRoutes.includes(route.id)"
                            title="Bearbeiten"
                            :actions="getRouteActions(route)"
                            :smallIcon="true"
                            :expandClickableArea="true"
                            :tooltips="tooltipsContent"
                            :tracking-type="trackingType"
                            @edit="editHandler(route)"
                            @remove="removeHandler(route)"
                            @traffic-alert="trafficAlertHandler(route)"
                        />
                        <Loader v-else />
                    </div>
                </div>
            </div>

             <div v-if="!savedRoutes.length && mode==='summary'" class="saved-routes__empty">
                 <icon :width="mobile ? '38' : '40'" :height="mobile ? '38' : '40'" :view-box=" mobile ? '0 0 38 38' : '0 0 40 40'"><IconStarOutlineGray/></icon>
                 <div class="saved-routes__empty__text">
                     <div class="copy-medium">Noch keine gespeicherten Routen</div>
                     <div class="copy-light">Route mit Klick auf Stern speichern</div>
                 </div>
            </div>
            <SmrButton @click.native="showAllClickHandler" color="grey" v-if="mode === 'summary' && savedRoutes.length">
                Alle anzeigen
            </SmrButton>
        </div>
    </div>
</template>

<script>
import * as api from '@/api';
import { mapGetters } from 'vuex';
import Sortable from 'sortablejs';
import { unprepareRouteFavouriteOptions } from '@/utils/route-utils'
import eventBus from '@/utils/event-bus';
import SmrButton from '@/components/SmrButton.vue'
import SmrTooltip from '@/components/SmrTooltip.vue'
import Icon from '@/components/Icon.vue'
import IconDragHandle from '@/components/icons/IconDragHandle.vue'
import IconStarOutlineGray from './icons/IconStarOutlineGray.vue';
import IconBell from '@/components/icons/IconBell.vue'
import TransportModeIcon from '@/components/TransportModeIcon.vue'
import WaypointSummary from '@/components/WaypointSummary.vue'
import MoreActions from '@/components/MoreActions.vue'
import { trackEvent, trackGa4Event } from '@/utils/tracking'
import { getRouteParams } from '@/utils/route-params'
import Loader from '@/components/Loader.vue'
import { TRAFFIC_ALERT_TRANSPORT_MODES } from '@/data/transportModesTypes'

export default {
    components: {
        SmrButton,
        SmrTooltip,
        Icon,
        IconDragHandle,
        IconStarOutlineGray,
        IconBell,
        TransportModeIcon,
        WaypointSummary,
        MoreActions,
        Loader,
    },
    props: {
        mode: {
            type: String,
            default: 'full'
        }
    },
    data() {
        return {
            sortable: false,
            actions: [
                {
                    'icon': 'IconEdit',
                    'label': 'Route umbenennen',
                    'event': 'edit'
                },
                {
                    'icon': 'IconBell',
                    'label': 'Traffic Alert einrichten',
                    'event': 'traffic-alert'
                },
                {
                    'icon': 'IconDelete',
                    'label': 'Route löschen',
                    'event': 'remove'
                },
            ],
            tooltipsContent: {
                moreActions: {
                    content: 'Weitere Optionen',
                    position: 'bottom-start'
                }
            },
            trackingType: 'savedRoutes',
            loadingRoutes: [],
        }
    },
    computed: {
        ...mapGetters(['savedRoutes', 'user', 'mobile', 'routeTrafficAlerts']),
        routes() {
            if(!this.savedRoutes) return false;
            let routes = [...this.savedRoutes];
            routes = routes.filter(r => r !== undefined)
            routes = routes.reverse()
            routes.forEach(r => r.loading = false)
            if(this.mode === 'summary' && routes.length > 3) {
                routes.length = 3;
            }
            routes.forEach(route => {
                route.hasTrafficAlert = this.hasTrafficAlert(route)
            })
            return routes;
        },
        userLoggedIn() {
            return this.user;
        }
    },
    mounted() {
        if(this.mode === 'full') {
            this.initSortable();
        }
    },
    methods: {
        actionsClickHandler(e) {
            e.stopPropagation();
        },
        showAllClickHandler() {
            trackEvent({ category: 'Favorites', action: 'Favorite Routes', label: 'See More' });
            trackGa4Event({ga4Parameter1Name: 'options', ga4Parameter1Value: 'see_more', ga4Event: 'favorite_routes'})
            this.$store.commit('setSavedRoutesListOpen', true)
        },
        editHandler(route) {
            eventBus.$emit('showFavouritesPanel', {
                type: 'route',
                ...route.completeRoute
            });
        },
        removeHandler(route) {
            eventBus.$emit('showFavouriteRemovalConfirmation', () => { 
                this.$store.dispatch('removeSavedRoute', route.id)
                if(route.hasTrafficAlert) {
                    this.$store.dispatch('removeRouteTrafficAlert', route.completeRoute.gis2Id)
                }
            }, 'route')
        },
        
        trafficAlertHandler(route) {
            if(route.hasTrafficAlert) {
                eventBus.$emit('showTrafficAlertPage', route.completeRoute.gis2Id)
            } else {
                this.createRouteSnapshot(route)
            }
        },
        initSortable() {
            // let self = this;
            if(!this.sortable) return
            const searchFieldsEl = this.$refs['saved-routes-list'];
            Sortable.create(searchFieldsEl, {
                animation: 150,
                // handle: self.mobile ? false : '.saved-routes__route__drag-handle',
                delayOnTouchOnly: true,
                delay: 200,
                touchStartThreshold: 20,
                onChoose() {
                },
                onUnchoose() {
                },
                onEnd(e) {
                    console.log('option moved', e);
                    // self.$emit('optionMoved', {oldIndex: e.oldIndex, newIndex: e.newIndex}); // todo: really change order in the API
                },
            });
        },
        routeClickHandler(route) {
            trackEvent({ category: 'Favorites', action: 'Favorite Routes', label: 'Select' });
            trackGa4Event({ga4Parameter1Name: 'select_poi', ga4Parameter1Value: 'route', ga4Event: 'favorite_routes'})
            this.submitSavedRoute(route);
        },
        submitSavedRoute(route) {
            const routeParameters = route.completeRoute.routeParameters
            const savedRouteOptions = unprepareRouteFavouriteOptions(routeParameters, route.mode)
            const routeWaypoints = [...route.waypoints].map(waypoint => {
                const newWaypoint = waypoint
                if(!waypoint.type) newWaypoint.type = 'GEO_ADDRESS'
                return newWaypoint
            })
            if(savedRouteOptions) {
                this.$store.commit('setSavedRouteSettings', savedRouteOptions.settings)
                this.$store.commit('setSavedRouteTime', savedRouteOptions.time)
            }
            this.$store.commit('setSavedRouteClicked', true)
            this.$store.commit('setSavedRouteSettingsChanges', savedRouteOptions.settings)
            this.$store.commit('setSavedRouteInfo', { id: route.id, waypoints: routeWaypoints, mode: route.mode, note: route.completeRoute.note, interimStopsLoaded: route.waypoints.length > 2 ? false : true });
            this.$store.commit('setSavedRoutesListOpen', false)
        },
        hasTrafficAlert(route) {
            if(this.routeTrafficAlerts) {
                const matchingRoute = this.routeTrafficAlerts.find(alert => alert.gisId === route.completeRoute.gis2Id)
                return matchingRoute && matchingRoute.monitoringConfigs && matchingRoute.monitoringConfigs.length > 0 ? true : false
            }
            return false
        },
        getRouteActions(route) {
            let actions = JSON.parse(JSON.stringify(this.actions))
            if(!TRAFFIC_ALERT_TRANSPORT_MODES.includes(route.mode)) {
                actions = actions.filter((action) => action.event !== 'traffic-alert')
            }
            if(route.hasTrafficAlert) {
                const trafficAlertActionIndex = actions.findIndex(action => action.event === 'traffic-alert')
                if(trafficAlertActionIndex > 0) actions[trafficAlertActionIndex].label = 'Traffic Alert öffnen'
            }
            return actions
        },
        async createRouteSnapshot(route) {
            this.loadingRoutes.push(route.id)
            try {
                // need to first call the route API to get the linestring
                const savedRouteParameters = route.completeRoute.routeParameters
                const savedRouteOptions = unprepareRouteFavouriteOptions(savedRouteParameters, route.mode)
                let routeParams = getRouteParams(route.mode, false, savedRouteOptions.settings)
                routeParams.waypoints = route.waypoints.map(waypoint => {
                    return {
                        id: waypoint.id,
                        label: waypoint.name,
                        coordinate: {
                            latitude: waypoint.lat,
                            longitude: waypoint.lon
                        },
                    }
                })
                routeParams = {...routeParams, ...savedRouteOptions.time}
                const routeResponse = await api.smr.route(routeParams)
                const routeData = routeResponse.data
                const trafficAlertDetails = {
                    duration: routeData.journeys[0].metadata.duration,
                    distance: routeData.journeys[0].metadata.distance,
                }
                if(routeData.journeys[0].sections.length <= 1) {
                    trafficAlertDetails.linestring = {
                        data: routeData.journeys[0].sections[0].polylineWKT
                    }
                } else {
                    trafficAlertDetails.linestrings = routeData.journeys[0].sections.map(section => {
                        return { data: section.polylineWKT }
                    })
                }                // after, create the snapshot
                const snapshotResponse = await api.favourites.addRouteSnapshot(route.id, trafficAlertDetails)
                if(snapshotResponse.data) {
                    this.loadingRoutes = this.loadingRoutes.filter(r => r !== route.id)
                    eventBus.$emit('showTrafficAlertPage', route.completeRoute.gis2Id)
                }
            } catch(error) {
                console.error(error)
                this.loadingRoutes = this.loadingRoutes.filter(r => r !== route.id)
            }
        }
    }
}
</script>

<style lang="scss">
.saved-routes {
    $block: &;
    padding-top: 16px;
    padding-bottom: 16px;
    &__header {
        margin-bottom: 4px;
    }
    &__intro {
        margin-bottom: 16px;
    }
    &__list {
        &:not(:last-child) {
            margin-bottom: 24px;
        }
    }
    &__route {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding-top: 12px;
        cursor: pointer;
        background: white;
        overflow: hidden;
        .no-touch &:hover {
            #{$block}__route__drag-handle {
                visibility: visible;
            }
        }
        &:not(:last-child) {
            padding-bottom: 12px;

            &::before {
                content: '';
                display: block;
                position: absolute;
                bottom: 0;
                right: -16px;
                width: calc(100% - 40px); // 100% width minus the yellow icon minus its margin plus the side section padding
                height: 1px;
                background: $color-black-005;
                z-index: 1;
            }
        }
        &:after{
            content: "";
            position: absolute;
            width: 74%;
            height: 100%;
            background-image: -webkit-linear-gradient(left, rgba(255, 255, 255, 0.2) 0%, white 26%, white 77%);
            /* right: -1px; */
            left: 70%;
        }
        &__data {
            display: flex;
            position: relative;
            &__icon {
                @include yellow-icon-square;
                flex-shrink: 0;
            }
            &__info {
                &__name {
                    margin-bottom: 3px;

                    &__icon {
                        margin-right: 2px;
                        transform: translateY(2px)
                    }
                }
            }
        }
        &__actions {
            // position: relative;
            position: absolute;
            right: 0;
            display: flex;
            align-items: center;
            z-index: 1;
            svg {
                display: block;
            }
        }
        &__drag-handle {
            color: $color-grey;
            cursor: pointer;
            cursor: grab;
            margin-right: 6px;
            @include mq(tablet) {
                position: absolute;
                top: 50%;
                right: 100%;
                margin-top: -10px;
            }
            .no-touch & {
                visibility: hidden;
            }
            .sortable-chosen & {
                cursor: grabbing;
            }
            .no-touch &:hover {
                color: $color-black;
            }
        }
        
    }

    &__empty {
        margin-top: 18px;
        display: flex;
        flex-direction: row;

        &__text {
            margin-left: 16px;
            display: fle;
            flex-direction: column;
        }
    }
}
</style>